/*
 * voice-labels/core.js — what a voice label IS, without a browser.
 *
 * A label is a voice take and/or a short text, plus an id that the printed
 * sticker carries. Everything here is pure (no DOM, no IndexedDB, no audio
 * API) so node drives it directly: tests/js/voice-labels-core.test.mjs.
 *
 * THE STICKER IS A LINK. Until 2026-10-22 the sticker held `TIMBRICA_VL:<id>`
 * drawn by a hand-made "QR-like" grid with no error correction and no format
 * bits — no reader decoded it, ours included (measured: zxing returned nothing
 * on the tool's own code, everything on a real QR of the same text). Now the
 * code is a real QR of an ordinary URL:
 *
 *     https://timbrica.com/ru/voice-labels#vl1.<id>.<lang>.<text>.<expires>
 *
 * so ANY camera app opens the tool on the label, and the part after `#` never
 * reaches a server (a fragment is not sent). Fields:
 *   id      — 8 chars [0-9a-z]; empty for a text-only label
 *   lang    — BCP-47 primary tag the text is spoken in ('ru', 'en', …)
 *   text    — the sticker text, UTF-8 → base64url. It is the label's voice on
 *             a phone that does not hold the recording: speech synthesis reads
 *             it. Empty when the label has no text.
 *   expires — YYYYMMDD or empty
 * Trailing empty fields are dropped, so a voice-only label is `#vl1.<id>` and
 * its code stays small enough for a 25 mm sticker.
 *
 * The old `TIMBRICA_VL:<id>` text is still understood when it is read.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.VoiceLabelsCore = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null)), function () {
    'use strict';

    var FORMAT = 'vl1';
    var LEGACY_PREFIX = 'TIMBRICA_VL:';
    var TEXT_MAX = 60;          // characters on the sticker and in the code
    var ID_RE = /^[0-9A-Za-z_]{3,40}$/;   // new ids are 8 × [0-9a-z]; '_' — labels made before 2026-10-22 (vl_…)

    /* ── id ─────────────────────────────────────────────────────────────── */
    function newId(rand) {
        var alphabet = '0123456789abcdefghijklmnopqrstuvwxyz';
        var out = '';
        var bytes = null;
        if (!rand) {
            try {
                var c = (typeof crypto !== 'undefined' && crypto.getRandomValues) ? crypto : null;
                if (c) { bytes = new Uint8Array(8); c.getRandomValues(bytes); }
            } catch (e) { bytes = null; }
        }
        for (var i = 0; i < 8; i++) {
            var r = bytes ? bytes[i] : Math.floor((rand ? rand() : Math.random()) * 256);
            out += alphabet[r % 36];
        }
        return out;
    }

    /* ── UTF-8 ↔ base64url ──────────────────────────────────────────────── */
    function utf8Bytes(str) {
        str = String(str == null ? '' : str);
        if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str);
        var out = [];
        for (var i = 0; i < str.length; i++) {
            var cp = str.codePointAt(i);
            if (cp > 0xffff) i++;
            if (cp < 0x80) out.push(cp);
            else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
            else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
            else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
        }
        return new Uint8Array(out);
    }
    function utf8String(bytes) {
        if (typeof TextDecoder !== 'undefined') return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
        var s = '';
        for (var i = 0; i < bytes.length; i++) s += '%' + ('0' + bytes[i].toString(16)).slice(-2);
        try { return decodeURIComponent(s); } catch (e) { return ''; }
    }
    var B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    function b64urlEncode(str) {
        var b = utf8Bytes(str), out = '';
        for (var i = 0; i < b.length; i += 3) {
            var n = (b[i] << 16) | ((b[i + 1] || 0) << 8) | (b[i + 2] || 0);
            out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
            if (i + 1 < b.length) out += B64[(n >> 6) & 63];
            if (i + 2 < b.length) out += B64[n & 63];
        }
        return out;
    }
    function b64urlDecode(s) {
        s = String(s || '').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        var bytes = [], buf = 0, bits = 0;
        for (var i = 0; i < s.length; i++) {
            var v = B64.indexOf(s[i]);
            if (v < 0) return null;
            buf = (buf << 6) | v; bits += 6;
            if (bits >= 8) { bits -= 8; bytes.push((buf >> bits) & 255); }
        }
        return utf8String(new Uint8Array(bytes));
    }

    /* ── text ───────────────────────────────────────────────────────────── */
    /* One line, no control characters, at most TEXT_MAX code points. */
    function cleanText(text) {
        var s = String(text == null ? '' : text).replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
        var cps = Array.from ? Array.from(s) : s.split('');
        if (cps.length > TEXT_MAX) s = cps.slice(0, TEXT_MAX).join('').trim();
        return s;
    }

    /* The language the text is spoken in. Script decides where it can (Hangul,
     * Arabic, Ukrainian letters); Latin and Cyrillic otherwise fall to the page
     * language when it uses that script, else to the script's main language. */
    var LATIN_LOCALES = { en: 1, de: 1, es: 1, fr: 1, pt: 1, id: 1 };
    function guessLang(text, pageLocale) {
        var s = String(text || '');
        var loc = String(pageLocale || 'en').toLowerCase().slice(0, 2);
        if (/[가-힯ᄀ-ᇿ]/.test(s)) return 'ko';
        if (/[؀-ۿ]/.test(s)) return 'ar';
        if (/[Ѐ-ӿ]/.test(s)) {
            // Ukrainian or Russian: the site's one detector (timbrica-core), not «is there an і» —
            // «Сушёные яблоки из Київа» is Russian. No sign either way: the page's language.
            var cl = typeof window !== 'undefined' && window ? window.__cyrillicLang : null;
            var fb = loc === 'uk' ? 'uk' : 'ru';
            var cy = typeof cl === 'function' ? cl(s, { fallback: fb }) : fb;
            return cy === 'uk' ? 'uk' : 'ru';
        }
        if (/[a-z]/i.test(s)) return LATIN_LOCALES[loc] ? loc : 'en';
        return loc;
    }

    /* ── dates (local calendar days, YYYY-MM-DD) ────────────────────────── */
    function pad2(n) { return (n < 10 ? '0' : '') + n; }
    function ymd(d) { return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); }
    function parseYmd(s) {
        var m = /^(\d{4})-?(\d{2})-?(\d{2})$/.exec(String(s || ''));
        if (!m) return null;
        var d = new Date(+m[1], +m[2] - 1, +m[3]);
        if (d.getFullYear() !== +m[1] || d.getMonth() !== +m[2] - 1 || d.getDate() !== +m[3]) return null;
        return d;
    }
    function normDate(s) { var d = parseYmd(s); return d ? ymd(d) : ''; }
    function addDays(from, n) {
        var d = from instanceof Date ? new Date(from.getFullYear(), from.getMonth(), from.getDate()) : parseYmd(from);
        if (!d) return '';
        d.setDate(d.getDate() + n);
        return ymd(d);
    }
    /* Calendar month: 31 January + 1 month = 28/29 February, not 2/3 March. */
    function addMonths(from, n) {
        var d = from instanceof Date ? new Date(from.getFullYear(), from.getMonth(), from.getDate()) : parseYmd(from);
        if (!d) return '';
        var day = d.getDate();
        d.setDate(1);
        d.setMonth(d.getMonth() + n);
        var last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        d.setDate(Math.min(day, last));
        return ymd(d);
    }
    /* Whole calendar days from `now` to the expiry date (negative: past). */
    function expiryInfo(expires, now) {
        var d = parseYmd(expires);
        if (!d) return null;
        var t = now instanceof Date ? now : new Date(now == null ? Date.now() : now);
        var today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
        var days = Math.round((d - today) / 86400000);
        var state = days < 0 ? 'expired' : days === 0 ? 'today' : days <= 3 ? 'soon' : 'ok';
        return { days: days, state: state, date: ymd(d) };
    }

    /* ── the code ───────────────────────────────────────────────────────── */
    function buildPayload(label) {
        label = label || {};
        var id = ID_RE.test(String(label.id || '')) ? String(label.id) : '';
        var text = cleanText(label.text);
        var lang = text ? String(label.lang || '').toLowerCase().replace(/[^a-z-]/g, '').slice(0, 8) : '';
        var exp = normDate(label.expires).replace(/-/g, '');
        var parts = [FORMAT, id, lang, text ? b64urlEncode(text) : '', exp];
        while (parts.length > 2 && parts[parts.length - 1] === '') parts.pop();
        return parts.join('.');
    }
    function buildUrl(base, label) {
        return String(base || '').replace(/#.*$/, '') + '#' + buildPayload(label);
    }

    /* What a scanned string means. Accepts:
     *   - our URL on any host and locale (a sticker printed from another
     *     locale or the dev box still plays), path ending in /voice-labels;
     *   - the bare fragment ('#vl1…' or 'vl1…');
     *   - the legacy 'TIMBRICA_VL:<id>'.
     * Anything else is null: a foreign QR is not ours to speak. */
    function parseScan(raw) {
        var s = String(raw == null ? '' : raw).trim();
        if (!s) return null;
        if (s.indexOf(LEGACY_PREFIX) === 0) {
            var lid = s.slice(LEGACY_PREFIX.length).trim();
            return /^[0-9a-z_]{3,40}$/i.test(lid) ? { kind: 'legacy', id: lid, text: '', lang: '', expires: '' } : null;
        }
        var frag = s;
        var hashAt = s.indexOf('#');
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) {
            if (hashAt < 0) return null;
            var path = s.slice(0, hashAt).replace(/[?].*$/, '').replace(/\/+$/, '');
            if (!/\/voice-labels$/i.test(path)) return null;
            frag = s.slice(hashAt + 1);
        } else if (hashAt === 0) {
            frag = s.slice(1);
        }
        try { frag = decodeURIComponent(frag); } catch (e) { /* keep as is */ }
        var p = frag.split('.');
        if (p[0] !== FORMAT) return null;
        var id = p[1] || '';
        if (id && !ID_RE.test(id)) return null;
        var text = p[3] ? b64urlDecode(p[3]) : '';
        if (text === null) return null;
        text = cleanText(text);
        var exp = p[4] ? normDate(p[4]) : '';
        if (!id && !text) return null;
        return { kind: 'vl', id: id, lang: (p[2] || '').toLowerCase(), text: text, expires: exp };
    }

    /* Side of the printed code that a phone still reads when the person can
     * not aim it: 0.6 mm per module (lab phones read 0.4 at 15 cm; the margin
     * is for a hand that wanders) plus a 2-module quiet zone each side. */
    function minPrintMm(moduleCount) {
        var n = Math.max(21, moduleCount | 0);
        return Math.ceil((n + 4) * 0.6);
    }

    /* ── duration ───────────────────────────────────────────────────────── */
    function fmtDuration(sec) {
        sec = Number(sec) || 0;
        sec = sec > 0 && sec < 1 ? 1 : Math.max(0, Math.round(sec));   // a word shorter than a second is «0:01», not «0:00»
        return Math.floor(sec / 60) + ':' + pad2(sec % 60);
    }

    /* ── backups ────────────────────────────────────────────────────────── */
    var BACKUP_FORMAT = 'timbrica-voice-labels';
    /* One row as it travels in a backup: audio as a data URL so a backup is a
     * single self-contained JSON file any device opens. */
    function normalizeRow(r) {
        if (!r || typeof r !== 'object') return null;
        var id = String(r.id || '');
        if (!id || !/^[0-9a-z_]{3,40}$/i.test(id)) return null;
        var audio = typeof r.audio === 'string' && /^data:audio\//i.test(r.audio) ? r.audio : '';
        var text = cleanText(r.text != null ? r.text : r.description);
        if (!audio && !text) return null;
        var created = Number(r.createdAt) || 0;
        return {
            id: id,
            audio: audio,
            text: text,
            lang: String(r.lang || '').toLowerCase().slice(0, 8),
            duration: Math.max(0, Number(r.duration) || 0),
            expires: normDate(r.expires),
            createdAt: created,
            updatedAt: Number(r.updatedAt) || created,
        };
    }
    function parseBackup(json) {
        var data = json;
        if (typeof json === 'string') {
            try { data = JSON.parse(json); } catch (e) { return null; }
        }
        var rows = Array.isArray(data) ? data
            : (data && data.format === BACKUP_FORMAT && Array.isArray(data.labels)) ? data.labels : null;
        if (!rows) return null;
        var out = [];
        for (var i = 0; i < rows.length; i++) { var n = normalizeRow(rows[i]); if (n) out.push(n); }
        return out;
    }
    function backupDocument(rows, meta) {
        return { format: BACKUP_FORMAT, v: 2, exportedAt: (meta && meta.at) || Date.now(), count: rows.length, labels: rows };
    }
    /* Which incoming rows to write: new ids, and ids whose copy is newer. */
    function merge(existing, incoming) {
        var have = {};
        (existing || []).forEach(function (r) { if (r && r.id) have[r.id] = r; });
        var put = [], added = 0, updated = 0, skipped = 0;
        (incoming || []).forEach(function (r) {
            var cur = have[r.id];
            if (!cur) { put.push(r); added++; return; }
            if ((Number(r.updatedAt) || 0) > (Number(cur.updatedAt) || Number(cur.createdAt) || 0)) { put.push(r); updated++; return; }
            skipped++;
        });
        return { put: put, added: added, updated: updated, skipped: skipped };
    }

    /* ── audio: where the voice is ──────────────────────────────────────── */
    /* First and last sample worth keeping: 20-ms frames whose RMS is within
     * 34 dB of the loudest frame count as voice; 160 ms of air stays before
     * and 260 ms after, so a soft first consonant and the tail of the last
     * word are not shaved. A take with no voice frame is returned whole. */
    function trimBounds(samples, sampleRate, opts) {
        opts = opts || {};
        var n = samples ? samples.length : 0;
        var sr = sampleRate || 48000;
        if (!n) return { start: 0, end: 0 };
        var frame = Math.max(1, Math.round(sr * 0.02));
        var rms = [], peak = 0;
        for (var s = 0; s < n; s += frame) {
            var e = Math.min(n, s + frame), sum = 0;
            for (var i = s; i < e; i++) sum += samples[i] * samples[i];
            var v = Math.sqrt(sum / (e - s));
            rms.push(v);
            if (v > peak) peak = v;
        }
        if (!(peak > 1e-4)) return { start: 0, end: n };
        var thr = peak * Math.pow(10, -(opts.rangeDb || 34) / 20);
        var first = -1, last = -1;
        for (var k = 0; k < rms.length; k++) { if (rms[k] >= thr) { if (first < 0) first = k; last = k; } }
        if (first < 0) return { start: 0, end: n };
        var start = Math.max(0, first * frame - Math.round(sr * (opts.headS || 0.16)));
        var end = Math.min(n, (last + 1) * frame + Math.round(sr * (opts.tailS || 0.26)));
        return { start: start, end: end };
    }

    /* Peaks for a waveform of `bars` columns: max |sample| per column. */
    function peaks(samples, bars) {
        var n = samples ? samples.length : 0, out = new Array(bars);
        for (var b = 0; b < bars; b++) {
            var s = Math.floor(b * n / bars), e = Math.max(s + 1, Math.floor((b + 1) * n / bars)), m = 0;
            for (var i = s; i < e && i < n; i++) { var a = samples[i] < 0 ? -samples[i] : samples[i]; if (a > m) m = a; }
            out[b] = m;
        }
        return out;
    }

    /* "Stop when the person stops talking." Fed one level (dBFS of a short
     * frame) at a time; answers 'stop' once voice was heard for `minVoiceMs`
     * and has then been quiet for `holdMs`. The quiet line follows the room:
     * the noise floor is the lowest level seen, creeping up 1.5 dB/s so a fan
     * that starts mid-take does not hold the recorder open forever. */
    function silenceStopper(opts) {
        opts = opts || {};
        var holdMs = opts.holdMs || 2000;
        var minVoiceMs = opts.minVoiceMs || 350;
        var floor = Infinity, voiceMs = 0, quietMs = 0, lastT = null;
        return {
            feed: function (levelDb, tMs) {
                var dt = lastT == null ? 0 : Math.max(0, Math.min(250, tMs - lastT));
                lastT = tMs;
                if (!isFinite(levelDb)) levelDb = -120;
                if (levelDb < floor) floor = levelDb; else floor += 1.5 * dt / 1000;
                var voiceLine = Math.max(floor + 14, -58);
                var quietLine = Math.max(floor + 7, -64);
                if (levelDb >= voiceLine) { voiceMs += dt; quietMs = 0; }
                else if (levelDb < quietLine) quietMs += dt;
                if (voiceMs >= minVoiceMs && quietMs >= holdMs) return 'stop';
                return voiceMs >= minVoiceMs ? 'voice' : 'wait';
            },
            heardVoice: function () { return voiceMs >= minVoiceMs; },
        };
    }

    return {
        FORMAT: FORMAT,
        TEXT_MAX: TEXT_MAX,
        BACKUP_FORMAT: BACKUP_FORMAT,
        newId: newId,
        b64urlEncode: b64urlEncode,
        b64urlDecode: b64urlDecode,
        cleanText: cleanText,
        guessLang: guessLang,
        ymd: ymd,
        normDate: normDate,
        addDays: addDays,
        addMonths: addMonths,
        expiryInfo: expiryInfo,
        buildPayload: buildPayload,
        buildUrl: buildUrl,
        parseScan: parseScan,
        minPrintMm: minPrintMm,
        fmtDuration: fmtDuration,
        normalizeRow: normalizeRow,
        parseBackup: parseBackup,
        backupDocument: backupDocument,
        merge: merge,
        trimBounds: trimBounds,
        peaks: peaks,
        silenceStopper: silenceStopper,
    };
});
